import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { StatusDot } from "@/components/ui/badge";
import {
  LineGrid,
  LineGridBody,
  LineGridHead,
  LineGridRow,
  LineGridTd,
  LineGridTh,
} from "@/components/shared/line-grid";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  BATCH_STATUSES,
  batchStatusTone,
  fmtQty,
  labelFor,
  qualityTone,
  QUALITY_STATUSES,
} from "@/features/inventory/constants";
import {
  compatibleInventoryHint,
  filterCompatibleInventory,
  isPlanningSelectableBatch,
  type PlanningStockBatch,
} from "@/lib/mill";
import { cn } from "@/lib/utils";

type PickerBatch = PlanningStockBatch;

/**
 * v1-style inventory table: process-compatible stock, dense filters, multi-select → stage inputs.
 */
export function StageInventoryPicker({
  stageProcessCode,
  batches,
  selectedIds,
  editable,
  onSelectionChange,
}: {
  stageProcessCode: string | null | undefined;
  batches: PickerBatch[];
  selectedIds: string[];
  editable: boolean;
  onSelectionChange: (rows: PickerBatch[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [thkFilter, setThkFilter] = useState("");
  const [widthFilter, setWidthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"eligible" | "all" | "in_stock" | "reserved">(
    "eligible",
  );

  const compatible = useMemo(
    () => filterCompatibleInventory(batches, stageProcessCode),
    [batches, stageProcessCode],
  );

  const filtered = useMemo(() => {
    let rows = compatible;
    if (statusFilter === "in_stock") {
      rows = rows.filter((b) => b.batch_status === "in_stock");
    } else if (statusFilter === "reserved") {
      rows = rows.filter((b) => b.batch_status === "reserved");
    } else if (statusFilter === "all") {
      rows = batches.filter(isPlanningSelectableBatch);
    }

    const q = search.trim().toLowerCase();
    const thk = thkFilter.trim() ? Number(thkFilter) : null;
    const w = widthFilter.trim() ? Number(widthFilter) : null;

    return rows.filter((b) => {
      if (q) {
        const hay = [
          b.batch_no,
          b.material_name,
          b.grade_name,
          b.last_process_code,
          b.location_code,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (thk != null && !Number.isNaN(thk)) {
        if (b.thickness == null || Math.abs(b.thickness - thk) > 0.001) return false;
      }
      if (w != null && !Number.isNaN(w)) {
        if (b.width == null || Math.abs(b.width - w) > 0.001) return false;
      }
      return true;
    });
  }, [compatible, batches, search, thkFilter, widthFilter, statusFilter]);

  function toggleRow(batch: PickerBatch) {
    if (!editable) return;
    const on = selectedIds.includes(batch.id);
    const next = on
      ? batches.filter((b) => selectedIds.includes(b.id) && b.id !== batch.id)
      : [...batches.filter((b) => selectedIds.includes(b.id)), batch];
    onSelectionChange(next);
  }

  const hint = compatibleInventoryHint(stageProcessCode);

  return (
    <div className="space-y-1.5 rounded-md border border-border bg-muted/15 p-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
            Available stock
          </div>
          <p className="text-2xs text-muted-foreground">{hint}</p>
        </div>
        <div className="text-2xs tabular-nums text-muted-foreground">
          <span className="font-medium text-foreground">{filtered.length}</span> shown ·{" "}
          <span className="font-medium text-foreground">{compatible.length}</span> eligible ·{" "}
          <span className="font-medium text-foreground">{selectedIds.length}</span> selected
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
        <div className="relative col-span-2 sm:col-span-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Batch, material, grade…"
            className="h-7 pl-7 text-2xs"
            disabled={!editable}
          />
        </div>
        <Input
          value={thkFilter}
          onChange={(e) => setThkFilter(e.target.value)}
          placeholder="Thk mm"
          className="h-7 text-2xs tabular-nums"
          disabled={!editable}
        />
        <Input
          value={widthFilter}
          onChange={(e) => setWidthFilter(e.target.value)}
          placeholder="Width mm"
          className="h-7 text-2xs tabular-nums"
          disabled={!editable}
        />
        <NativeSelect
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "eligible" | "all" | "in_stock" | "reserved")
          }
          className="h-7 text-2xs"
          disabled={!editable}
        >
          <option value="eligible">Eligible only</option>
          <option value="in_stock">In stock</option>
          <option value="reserved">Reserved</option>
          <option value="all">All selectable</option>
        </NativeSelect>
      </div>

      <div className="max-h-52 overflow-auto rounded border border-border bg-card">
        <LineGrid>
          <LineGridHead>
            <LineGridTh className="w-8" />
            <LineGridTh className="min-w-[8rem]">Batch</LineGridTh>
            <LineGridTh>Material</LineGridTh>
            <LineGridTh className="w-14" align="right">
              Thk
            </LineGridTh>
            <LineGridTh className="w-14" align="right">
              W
            </LineGridTh>
            <LineGridTh className="w-14" align="right">
              Wt
            </LineGridTh>
            <LineGridTh className="w-20">Last proc</LineGridTh>
            <LineGridTh className="w-16">QC</LineGridTh>
            <LineGridTh className="w-16">Status</LineGridTh>
          </LineGridHead>
          <LineGridBody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="py-4 text-center text-2xs text-muted-foreground"
                >
                  No compatible stock for this stage
                </td>
              </tr>
            ) : (
              filtered.map((b) => {
                const selected = selectedIds.includes(b.id);
                return (
                  <LineGridRow
                    key={b.id}
                    className={cn(
                      selected && "bg-primary/5",
                      b.batch_status === "reserved" && !selected && "bg-amber-50/50",
                    )}
                  >
                    <LineGridTd>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!editable}
                        onChange={() => toggleRow(b)}
                        onClick={(e) => e.stopPropagation()}
                        className="size-3.5"
                      />
                    </LineGridTd>
                    <LineGridTd className="font-medium tabular-nums">{b.batch_no}</LineGridTd>
                    <LineGridTd className="truncate text-muted-foreground">
                      {b.material_name ?? "—"}
                      {b.grade_name ? ` · ${b.grade_name}` : ""}
                    </LineGridTd>
                    <LineGridTd align="right" className="tabular-nums">
                      {fmtQty(b.thickness)}
                    </LineGridTd>
                    <LineGridTd align="right" className="tabular-nums">
                      {fmtQty(b.width)}
                    </LineGridTd>
                    <LineGridTd align="right" className="tabular-nums">
                      {fmtQty(b.weight ?? 0)}
                    </LineGridTd>
                    <LineGridTd className="text-2xs text-muted-foreground">
                      {b.last_process_code ?? "—"}
                    </LineGridTd>
                    <LineGridTd>
                      <StatusDot tone={qualityTone((b.quality_status ?? "pending") as "pending")}>
                        {labelFor(QUALITY_STATUSES, b.quality_status ?? "pending")}
                      </StatusDot>
                    </LineGridTd>
                    <LineGridTd>
                      <StatusDot tone={batchStatusTone((b.batch_status ?? "in_stock") as "in_stock")}>
                        {labelFor(BATCH_STATUSES, b.batch_status ?? "in_stock")}
                      </StatusDot>
                    </LineGridTd>
                  </LineGridRow>
                );
              })
            )}
          </LineGridBody>
        </LineGrid>
      </div>

      {selectedIds.length > 0 ? (
        <p className="text-2xs text-muted-foreground">
          Selected batches load into inputs below — adjust qty/weight if needed.
        </p>
      ) : null}
    </div>
  );
}
